import { sha256Hex } from './effectLayout';
import { distinctChannels, fixtureChannels } from './rig';
import { hasFineSibling, isLogicalChannel, requiredWidth, type SealedChannelFlags, type SealedTargetTransport } from './sealedChannels';
import type { CloudRig, FixtureDescriptor } from './types';

export interface BoundRig {
  rigId: string;
  activeRigVersionId: string;
  controllerId: string | null;
}

export interface RoomRigReply { rigs: BoundRig[] }

interface RigDocument {
  schema: string;
  rigId: string;
  versionId: string;
  targets: Record<string, SealedTargetTransport & {
    id: string;
    name: string;
    kind: string;
    worldPosition?: { x: number; y: number; z?: number };
    provenance?: { placementUid?: string };
    elements: Array<{ name?: string; channels: Array<SealedChannelFlags & { group?: string; subType?: string; dmxOffset?: number }> }>;
  }>;
}

/** The signed rig document is the source of the room drawing after K3a. */
export function projectRigDocument(document: RigDocument, binding: BoundRig, legacyDeviceOrder?: readonly string[]): CloudRig {
  if ((document.schema !== 'nuance.rig/1' && document.schema !== 'nuance.rig/2') || document.rigId !== binding.rigId
      || document.versionId !== binding.activeRigVersionId) {
    throw new Error('Room rig document does not match its binding.');
  }
  const targets = Object.values(document.targets);
  // A schema-1 controller loads Firestore devices[] in stored order; a published rig loads sorted target ids.
  const orderedTargets = legacyDeviceOrder === undefined
    ? targets.sort((left, right) => left.id < right.id ? -1 : left.id > right.id ? 1 : 0)
    : (() => {
      const byId = new Map<string, RigDocument['targets'][string]>();
      for (const target of targets) {
        const id = target.id.toLowerCase();
        if (byId.has(id)) throw new Error(`Room rig has duplicate target ${id}.`);
        byId.set(id, target);
      }
      const ordered = legacyDeviceOrder.map((deviceId) => {
        const id = deviceId.toLowerCase();
        const target = byId.get(id);
        if (!target) throw new Error(`Room rig device order names missing target ${id}.`);
        byId.delete(id);
        return target;
      });
      const missing = byId.keys().next().value;
      if (missing !== undefined) throw new Error(`Room rig target ${missing} is missing from device order.`);
      return ordered;
    })();
  const devices: FixtureDescriptor[] = orderedTargets.filter((target) => target.kind === 'lighting').map((target) => {
    const elements = target.elements.map((element, index) => ({
      index,
      name: element.name,
      // The controller's element keys: logical channels only, each key once (`elementSummariesFromFixture`).
      channels: distinctChannels(element.channels.filter(isLogicalChannel).map((channel) => ({
        group: channel.group,
        subType: channel.subType,
        bitWidth: requiredWidth(target, channel),
        hasFine: hasFineSibling(channel),
        coarseOffset: channel.dmxOffset,
        fineOffset: channel.fineDmxOffset,
      }))),
    }));
    return {
      deviceId: target.id,
      // The Scenes `/3` join key: a drawing names this placement uid and resolves to `deviceId` (the target the
      // wire addresses) through this projection only, since the controller's compact get_rig carries none.
      placementUid: target.provenance?.placementUid,
      name: target.name,
      kind: target.kind,
      position: target.worldPosition,
      elementCount: target.elements.length,
      elements,
      // The document stores channels only on elements; the controller still publishes a whole-device value
      // per distinct key, and a fixture node with no key list to read it against is refused (2026-10-08).
      representativeChannels: fixtureChannels(elements),
      isControllable: true,
    };
  });
  return { rigId: binding.rigId, activeRigVersionId: binding.activeRigVersionId,
    rigVersion: { devices } };
}

/** A layout artifact a rig version carries, as `getRigVersion` prints it: a signed READ URL plus its checks. */
interface RigOpenLayout {
  url: string;
  sha256: string;
  sizeBytes: number;
  schema: string;
  layoutDigest: string;
  elementCount: number;
}

/**
 * The platform's `getRigVersion` reply (`nuance.rig-open-result/1`, `functions/src/commands/rig.ts`).
 *
 * The layouts ride on this reply and nowhere else on the production path: the sealed rig document carries
 * no URL for them, so dropping them here leaves the room with no effect map (2026-10-06).
 */
export interface RigOpenResult {
  rigUrl: string;
  rigSha256: string;
  gdtfs?: Array<{ sha256: string; url: string }>;
  geometry?: { revision: number; state: Record<string, unknown> };
  recipe?: { sha256: string; url: string };
  effectLayout?: RigOpenLayout & { zoneCount?: number };
  schematicLayout?: RigOpenLayout;
}

function legacyDeviceOrderFromVersion(value: unknown, versionId: string): string[] | undefined {
  if (!value || typeof value !== 'object') throw new Error(`Room rig version ${versionId} is missing.`);
  const version = value as Record<string, unknown>;
  if (version.schema === 'nuance.rig/1' || version.schema === 'nuance.rig/2') return undefined;
  if (!Array.isArray(version.devices)) throw new Error(`Room rig version ${versionId} has no devices array.`);
  return version.devices.map((entry: unknown, index: number) => {
    const deviceId = entry && typeof entry === 'object' && 'deviceId' in entry
      ? (entry as { deviceId: unknown }).deviceId : undefined;
    if (typeof deviceId !== 'string' || !deviceId.trim()) {
      throw new Error(`Room rig version ${versionId} has no device id at index ${index}.`);
    }
    return deviceId.toLowerCase();
  });
}

export async function openBoundRoomRig(
  binding: BoundRig,
  open: (rigId: string, versionId: string) => Promise<RigOpenResult>,
  readVersion: (rigId: string, versionId: string) => Promise<unknown>,
): Promise<CloudRig> {
  const [result, version] = await Promise.all([
    open(binding.rigId, binding.activeRigVersionId),
    readVersion(binding.rigId, binding.activeRigVersionId),
  ]);
  const { rigUrl, rigSha256, effectLayout, schematicLayout } = result;
  const legacyDeviceOrder = legacyDeviceOrderFromVersion(version, binding.activeRigVersionId);
  const response = await fetch(rigUrl);
  if (!response.ok) throw new Error(`Room rig download returned ${response.status}.`);
  const bytes = await response.arrayBuffer();
  if (await sha256Hex(bytes) !== rigSha256) throw new Error('Room rig checksum does not match.');
  const rig = projectRigDocument(JSON.parse(new TextDecoder().decode(bytes)) as RigDocument, binding, legacyDeviceOrder);
  return {
    ...rig,
    ...(effectLayout ? { effectLayout } : {}),
    ...(schematicLayout ? { schematicLayout } : {}),
  };
}

export function boundRigVersion(value: { rigs?: Array<{ rigId: string; activeRigVersionId?: string; rigVersionId?: string }> } | undefined,
  rigId: string): string {
  const binding = value?.rigs?.find((entry) => entry.rigId === rigId);
  return binding?.activeRigVersionId ?? binding?.rigVersionId ?? '';
}
